/**
 * ChatPanel — replaces EvidenceList as the session's primary sidebar.
 *
 * One chronological thread of:
 *   - text bubbles from operator + field (with optional file attachments)
 *   - evidence cards (auto-posted by the backend on snapshot or composite-
 *     recording ingest; click to open the existing EvidenceViewerModal)
 *   - system notices (centred grey lines, e.g. "Recording started")
 *
 * Live updates arrive via the `chat/message` envelope on the LiveKit bus —
 * the panel subscribes through useSessionBus. REST is the source of truth
 * and fills the initial load + late-join recovery (any messages that landed
 * between mount and bus-ready also surface via `refetchInterval=8000`).
 *
 * Operator-side wiring; field-side gets a separate slide-in surfaced from
 * FieldRoom. Both call the same REST + bus.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { listChat, sendText, uploadDocument, CHAT_TOPIC, CHAT_MESSAGE_TYPE } from "@/lib/chat";
import { formatTime } from "@/lib/format";
import type { CommandBus, Envelope } from "@/lib/commandBus";
import { useSessionBus } from "@/lib/sessionBus";
import { playSound } from "@/lib/soundEffects";
import type { ChatAttachment, ChatMessage, ChatPage } from "@/lib/types";

import {
  CaptureMetadataModal,
  type CaptureKind,
} from "@/modules/evidence/CaptureMetadataModal";
import { EvidenceViewerModal } from "@/modules/evidence/EvidenceViewerModal";
import {
  SignDocumentModal,
  SignatureStatusCard,
  kickoffSignature,
} from "@/modules/signature";
import type { SignatureTransaction } from "@/lib/types";


type Props = {
  sessionId: string;
  /** Field-only — when present, REST calls go through the X-Field-Session-Token
   *  path and author rows stamp `author_participant`. */
  fieldSessionToken?: string;
  /** When provided, replaces the default useSessionBus bus lookup. The
   *  field side passes its own bus from inside the LiveKitRoom context
   *  where useSessionBus isn't populated. */
  bus?: CommandBus | null;
  /** When true, render the history but hide the composer. Used on
   *  closed/expired sessions where nothing new can be added. */
  readOnly?: boolean;
};


export function ChatPanel({
  sessionId,
  fieldSessionToken,
  bus: explicitBus,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const storeBus = useSessionBus((s) => s.bus);
  const bus = explicitBus ?? storeBus;
  const [openEvidenceId, setOpenEvidenceId] = useState<string | null>(null);
  const [signEvidenceId, setSignEvidenceId] = useState<string | null>(null);
  // Track an active SecuSign signature ceremony, keyed by the source
  // Evidence id, so the SignatureStatusCard renders under the chat
  // attachment that started it and survives a panel re-mount.
  const [activeSignatures, setActiveSignatures] = useState<
    Record<string, string>
  >({});
  // Field-side one-tap signing: the backend defaults the signer from
  // the participant's redeemed Invite, so we just POST with an empty
  // body and surface the status card.
  const fieldSignKickoff = useMutation({
    mutationFn: (evidenceId: string) =>
      kickoffSignature(evidenceId, { signers: [], language: "fr" }),
    onSuccess: (txn) => {
      setActiveSignatures((cur) => ({
        ...cur,
        [txn.source_evidence_id]: txn.id,
      }));
    },
  });
  const isFieldSide = !!fieldSessionToken;
  function onSignEvidence(evidenceId: string) {
    if (isFieldSide) {
      fieldSignKickoff.mutate(evidenceId);
      return;
    }
    setSignEvidenceId(evidenceId);
  }
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["session-chat", sessionId],
    queryFn: async () => listChat(sessionId, { fieldSessionToken }),
    // Falls back from bus to polling so a transient envelope drop is
    // self-healing; 8s matches the EvidenceList cadence the operator is
    // already used to.
    refetchInterval: 8000,
  });

  // Bus live-update — upsert into the query cache by id (envelope_msg_id
  // dedups text writes, autoincrement uuids dedup evidence/system rows).
  useEffect(() => {
    if (!bus) return;
    const unsub = bus.on<{ message: ChatMessage }>(
      `${CHAT_TOPIC}/${CHAT_MESSAGE_TYPE}`,
      (env: Envelope<{ message: ChatMessage }>) => {
        const incoming = env.payload?.message;
        if (!incoming?.id) return;
        qc.setQueryData<ChatPage | undefined>(
          ["session-chat", sessionId],
          (prev) => {
            if (!prev) return { results: [incoming], has_more: false };
            if (prev.results.some((m) => m.id === incoming.id)) return prev;
            return { ...prev, results: [...prev.results, incoming] };
          },
        );
        // Notification chime on inbound messages from the OTHER side.
        // FieldChatDrawer plays this when the drawer is collapsed; the
        // operator side never had it. With this, both ends hear the
        // chime when a peer sends a line. Skip self-authored rows so
        // the operator doesn't ping their own send.
        const isPeer = isFieldSide
          ? incoming.author_participant === null   // operator → field
          : incoming.author_user === null;         // field → operator
        if (isPeer) void playSound("notify");
      },
    );
    return () => {
      unsub();
    };
  }, [bus, qc, sessionId, isFieldSide]);

  const send = useMutation({
    mutationFn: async () => {
      const trimmed = draft.trim();
      if (!trimmed && pendingAttachments.length === 0) return null;
      const saved = await sendText({
        sessionId,
        body: trimmed,
        attachmentIds: pendingAttachments.map((a) => a.id),
        fieldSessionToken,
        broadcastBus: bus,
      });
      return saved;
    },
    onSuccess: (saved) => {
      if (!saved) return;
      setDraft("");
      setPendingAttachments([]);
      qc.setQueryData<ChatPage | undefined>(
        ["session-chat", sessionId],
        (prev) => {
          if (!prev) return { results: [saved], has_more: false };
          if (prev.results.some((m) => m.id === saved.id)) return prev;
          return { ...prev, results: [...prev.results, saved] };
        },
      );
    },
    onError: (e) => setErr((e as Error).message),
  });

  // Phase 1B-I — after an attachment uploads, pop CaptureMetadataModal
  // so the operator/field can name + describe the file before it sits
  // in chat. Default title comes from the original filename (sans
  // extension); empty title keeps the chip + filename in the bubble.
  const [renameTarget, setRenameTarget] = useState<{
    evidenceId: string;
    kind: CaptureKind;
    defaultTitle: string;
  } | null>(null);
  const attach = useMutation({
    mutationFn: async (file: File) => {
      const evidence = await uploadDocument({
        sessionId,
        file,
        fieldSessionToken,
      });
      // Kind from the actual evidence row (uploadDocument infers from
      // mime: image/* → snapshot, else document).
      const inferredKind: "snapshot" | "document" =
        evidence.mime?.startsWith("image/") ? "snapshot" : "document";
      const chip: ChatAttachment = {
        id: evidence.id,
        // Keep the original filename for the pending chip so the user
        // sees what they just picked, not a sha-prefixed storage key.
        // Backend serialiser sources it from device_jsonb.name on read.
        name: file.name,
        mime: evidence.mime,
        byte_size: evidence.byte_size,
        kind: inferredKind,
        storage_key: evidence.storage_key,
        status: evidence.status,
        created_at: evidence.created_at,
      };
      return { chip, fileName: file.name };
    },
    onSuccess: ({ chip, fileName }) => {
      setPendingAttachments((prev) => [...prev, chip]);
      // Pre-fill the rename modal with the filename (minus extension)
      // so the operator hits Save without typing for the common case.
      const dotIdx = fileName.lastIndexOf(".");
      const base = dotIdx > 0 ? fileName.slice(0, dotIdx) : fileName;
      setRenameTarget({
        evidenceId: chip.id,
        kind: chip.kind === "snapshot" ? "snapshot" : "annotation",
        defaultTitle: base,
      });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const messages = useMemo(
    () => sortedMessages(q.data?.results ?? []),
    [q.data?.results],
  );

  // Auto-scroll on new messages so the operator sees the latest line
  // without manually scrolling — matches every chat UI ever.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className={
          "-mx-1 flex-1 space-y-3 overflow-y-auto px-1 py-1" +
          // Read-only transcript (e.g. the media page): the messages are for
          // reading, not interacting — neutralise every card/attachment/sign
          // button so nothing is clickable. Text stays selectable (only the
          // buttons get pointer-events-none, not the message text).
          (readOnly
            ? " [&_button]:pointer-events-none [&_button]:cursor-default"
            : "")
        }
        aria-live="polite"
        aria-relevant="additions"
      >
        {q.isLoading && (
          <p className="text-xs text-nb-text-3">{t("ui.chatPanel.loading")}</p>
        )}
        {!q.isLoading && messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-xs text-nb-text-3">
            <span aria-hidden className="text-2xl">💬</span>
            <p>{t("ui.chatPanel.emptyTitle")}</p>
            <p className="text-[11px] text-nb-text-3">
              {t("ui.chatPanel.emptyHint")}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            onOpenEvidence={readOnly ? () => {} : setOpenEvidenceId}
            onSignEvidence={readOnly ? () => {} : onSignEvidence}
            activeSignatureId={
              m.payload_jsonb?.evidence_id
                ? activeSignatures[
                    (m.payload_jsonb.evidence_id as string) || ""
                  ] || null
                : null
            }
            onDismissSignature={(evidenceId) =>
              setActiveSignatures((cur) => {
                const next = { ...cur };
                delete next[evidenceId];
                return next;
              })
            }
          />
        ))}
      </div>

      {!readOnly && (
        <Composer
          draft={draft}
          setDraft={setDraft}
          pending={pendingAttachments}
          onAttach={(f) => attach.mutate(f)}
          onRemoveAttachment={(id) =>
            setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
          }
          onSubmit={() => {
            setErr(null);
            send.mutate();
          }}
          attaching={attach.isPending}
          sending={send.isPending}
        />
      )}

      {err && (
        <p role="alert" className="mt-1 text-xs text-cvp-danger">
          {err}
        </p>
      )}

      {openEvidenceId && (
        <EvidenceViewerModal
          evidenceId={openEvidenceId}
          onClose={() => setOpenEvidenceId(null)}
        />
      )}

      {signEvidenceId && (
        <SignDocumentModal
          evidenceId={signEvidenceId}
          sessionId={sessionId}
          onClose={() => setSignEvidenceId(null)}
          onLaunched={(txn: SignatureTransaction) =>
            setActiveSignatures((cur) => ({
              ...cur,
              [signEvidenceId]: txn.id,
            }))
          }
        />
      )}

      {renameTarget && (
        <CaptureMetadataModal
          evidenceId={renameTarget.evidenceId}
          sessionId={sessionId}
          kind={renameTarget.kind}
          defaultTitleOverride={renameTarget.defaultTitle}
          onClose={() => setRenameTarget(null)}
        />
      )}
    </div>
  );
}


function sortedMessages(rows: ChatMessage[]): ChatMessage[] {
  return [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at));
}


function MessageRow({
  message,
  onOpenEvidence,
  onSignEvidence,
  activeSignatureId,
  onDismissSignature,
}: {
  message: ChatMessage;
  onOpenEvidence: (id: string) => void;
  onSignEvidence: (id: string) => void;
  activeSignatureId: string | null;
  onDismissSignature: (evidenceId: string) => void;
}) {
  if (message.kind === "system") {
    return (
      <SystemNotice
        message={message}
        onOpenEvidence={onOpenEvidence}
      />
    );
  }
  if (message.kind === "evidence")
    return (
      <EvidenceCard
        message={message}
        onOpen={onOpenEvidence}
        onSign={onSignEvidence}
        activeSignatureId={activeSignatureId}
        onDismissSignature={onDismissSignature}
      />
    );
  return (
    <TextBubble
      message={message}
      onOpenEvidence={onOpenEvidence}
      onSignEvidence={onSignEvidence}
    />
  );
}


function SystemNotice({
  message,
  onOpenEvidence,
}: {
  message: ChatMessage;
  onOpenEvidence: (id: string) => void;
}) {
  const { t } = useTranslation();
  const event = (message.payload_jsonb?.event as string | undefined) ?? "event";
  const data =
    (message.payload_jsonb?.data as Record<string, unknown> | undefined) ?? {};

  // Phase 1B-D / v1.5 — richer cards for the signature.* lifecycle.
  // Recording / generic events stay as the compact one-liner.
  if (event.startsWith("signature.")) {
    return (
      <SignatureSystemCard
        event={event}
        data={data}
        createdAt={message.created_at}
        onOpenEvidence={onOpenEvidence}
      />
    );
  }
  return (
    <p className="text-center text-[11px] text-nb-text-3">
      {humanEvent(event, t)} · {humanTime(message.created_at)}
    </p>
  );
}


function SignatureSystemCard({
  event,
  data,
  createdAt,
  onOpenEvidence,
}: {
  event: string;
  data: Record<string, unknown>;
  createdAt: string;
  onOpenEvidence: (id: string) => void;
}) {
  const { t } = useTranslation();
  const sourceEvidenceId =
    typeof data.source_evidence_id === "string"
      ? data.source_evidence_id
      : null;
  const signedEvidenceId =
    typeof data.signed_evidence_id === "string"
      ? data.signed_evidence_id
      : null;
  const signerCount =
    typeof data.signer_count === "number" ? data.signer_count : null;

  const tone = {
    "signature.requested": "border-nb-accent/40 bg-nb-accent/5 text-nb-text",
    "signature.sent": "border-nb-mute/40 bg-nb-bg text-nb-text",
    "signature.completed": "border-nb-ok/40 bg-nb-ok/5 text-nb-ok",
    "signature.refused": "border-nb-danger/40 bg-nb-danger/5 text-nb-danger",
    "signature.cancelled": "border-nb-mute/40 bg-nb-bg text-nb-text-2",
  }[event] ?? "border-nb-border bg-nb-surface text-nb-text-2";

  const heading = {
    "signature.requested": t("ui.chatPanel.sigRequested"),
    "signature.sent": t("ui.chatPanel.sigSent"),
    "signature.completed": t("ui.chatPanel.sigCompleted"),
    "signature.refused": t("ui.chatPanel.sigRefused"),
    "signature.cancelled": t("ui.chatPanel.sigCancelled"),
  }[event] ?? humanEvent(event, t);

  return (
    <div
      className={`mx-auto my-1 max-w-[85%] rounded-md border p-2 text-xs ${tone}`}
      role="status"
      data-testid={`chat-signature-${event}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{heading}</span>
        <span className="font-mono text-[10px] text-nb-text-3">
          {humanTime(createdAt)}
        </span>
      </div>
      {signerCount !== null && event === "signature.requested" && (
        <div className="mt-0.5 text-[11px] text-nb-text-3">
          {t("ui.chatPanel.signerCount", { count: signerCount })}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
        {sourceEvidenceId && (
          <button
            type="button"
            className="rounded bg-nb-bg px-2 py-0.5 hover:bg-nb-surface"
            onClick={() => onOpenEvidence(sourceEvidenceId)}
            data-testid="chat-signature-open-source"
          >
            {t("ui.chatPanel.sourcePdf")}
          </button>
        )}
        {signedEvidenceId && (
          <button
            type="button"
            className="rounded bg-nb-bg px-2 py-0.5 hover:bg-nb-surface"
            onClick={() => onOpenEvidence(signedEvidenceId)}
            data-testid="chat-signature-open-signed"
          >
            {t("ui.chatPanel.signedPdf")}
          </button>
        )}
      </div>
    </div>
  );
}


function humanEvent(event: string, t: TFunction): string {
  switch (event) {
    case "recording.started":
      return t("ui.chatPanel.recordingStarted");
    case "recording.stopped":
      return t("ui.chatPanel.recordingStopped");
    default:
      return event.replace(/\./g, " ");
  }
}


function EvidenceCard({
  message,
  onOpen,
  onSign,
  activeSignatureId,
  onDismissSignature,
}: {
  message: ChatMessage;
  onOpen: (id: string) => void;
  /** When set + the evidence is a document (kind_hint=="document", a PDF
   *  in practice), renders a Sign button so the operator can dispatch it
   *  for electronic signature straight from the card. */
  onSign: (id: string) => void;
  activeSignatureId: string | null;
  onDismissSignature: (evidenceId: string) => void;
}) {
  const { t } = useTranslation();
  const evidenceId = (message.payload_jsonb?.evidence_id as string | undefined) ?? null;
  const kindHint = (message.payload_jsonb?.kind_hint as string | undefined) ?? "evidence";
  const author = authorLabel(message, t);
  if (!evidenceId) {
    return null;
  }
  // Phase 1B-I — operator-supplied metadata. body mirrors title;
  // payload_jsonb carries notes + keywords. Empty title falls back to
  // the kind-default headline so older sessions still render cleanly.
  const customTitle =
    (message.body && message.body.trim()) ||
    ((message.payload_jsonb?.title as string | undefined) ?? "").trim();
  const headline = customTitle || evidenceHeadline(kindHint, t);
  const notes =
    (message.payload_jsonb?.notes as string | undefined)?.trim() ?? "";
  const keywords =
    (message.payload_jsonb?.keywords as string | undefined)?.trim() ?? "";
  // Sign button surfaces for document-kind evidence only (a PDF in
  // practice; the backend rejects non-PDFs). Auto-posted media —
  // snapshots / whiteboards / recordings — is NOT signable, so those
  // cards stay button-free. PDFs the operator attaches to a text
  // message get the same affordance via AttachmentChip's `onSign` path.
  const canSign = kindHint === "document";
  return (
    <div className="space-y-1.5">
      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => onOpen(evidenceId)}
          className="flex w-full min-w-0 items-center gap-3 rounded-2xl border border-nb-border bg-nb-surface/40 px-3 py-2.5 text-left text-sm transition hover:border-nb-mint/60 hover:bg-nb-surface"
          aria-label={t("ui.chatPanel.openEvidence", { kind: kindHint })}
        >
          <EvidenceThumbnailOrIcon evidenceId={evidenceId} kindHint={kindHint} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-nb-text">
              {headline}
            </span>
            <span className="block text-[11px] text-nb-text-3">
              {author} · {humanTime(message.created_at)}
            </span>
            {notes && (
              <span className="mt-1 block whitespace-pre-line text-[11px] text-nb-text-2">
                {notes}
              </span>
            )}
            {keywords && (
              <span className="mt-1 flex flex-wrap gap-1">
                {keywords
                  .split(",")
                  .map((k) => k.trim())
                  .filter(Boolean)
                  .map((k) => (
                    <span
                      key={k}
                      className="rounded-full bg-nb-surface px-1.5 py-0.5 text-[10px] text-nb-text-2"
                    >
                      #{k}
                    </span>
                  ))}
              </span>
            )}
          </span>
          <span aria-hidden className="shrink-0 text-nb-mint">
            →
          </span>
        </button>
        {canSign && (
          <button
            type="button"
            onClick={() => onSign(evidenceId)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-nb-mint/40 bg-nb-mint/10 px-3 py-2 text-xs font-medium text-nb-mint transition hover:bg-nb-mint/20"
            aria-label={t("ui.chatPanel.signAriaLabel")}
            data-testid="chat-sign-electronically"
          >
            ✍ {t("ui.chatPanel.signElectronically")}
          </button>
        )}
      </div>
      {activeSignatureId && (
        <SignatureStatusCard
          signatureId={activeSignatureId}
          onDismiss={() => onDismissSignature(evidenceId)}
        />
      )}
    </div>
  );
}


/** Image-shaped evidence kinds get an actual thumbnail on the card
 *  instead of a generic icon — backlog §9.2 (2026-09-08): "no inline
 *  photo preview at all... seeing an actual photo means clicking
 *  through to EvidenceViewerModal one at a time." Scoped to snapshot +
 *  whiteboard (both stored as a rendered PNG, per apps/evidence/
 *  models.py's EvidenceKind); recording/clip/document are left on the
 *  icon (a video or PDF isn't a useful <img> thumbnail).
 *
 *  Reuses EvidenceViewerModal's exact query key (["evidence-detail",
 *  id]) so opening the card afterward is an instant cache hit instead
 *  of a second fetch. */
const THUMBNAIL_KINDS = new Set(["snapshot", "whiteboard"]);

function EvidenceThumbnailOrIcon({
  evidenceId,
  kindHint,
}: {
  evidenceId: string;
  kindHint: string;
}) {
  const wantsThumbnail = THUMBNAIL_KINDS.has(kindHint);
  const q = useQuery({
    queryKey: ["evidence-detail", evidenceId],
    queryFn: async () =>
      (await api.get<{ status: string; download_url: string | null }>(
        ApiPaths.evidence(evidenceId),
      )).data,
    enabled: wantsThumbnail,
    staleTime: 60_000,
  });
  const url =
    wantsThumbnail && q.data?.status === "ready" ? q.data.download_url : null;

  if (url) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        className="h-9 w-9 shrink-0 rounded-lg object-cover"
        // A presigned URL that 404s (expired) or a corrupt fetch
        // shouldn't leave a broken-image glyph in the chat thread —
        // fall back to hiding the <img>; the icon square underneath
        // (rendered by the sibling branch below on next render) is
        // simpler than juggling error state here, so we just blank it.
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
        }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nb-mint/15 text-base text-nb-mint"
    >
      {evidenceIcon(kindHint)}
    </span>
  );
}


function evidenceIcon(kind: string): string {
  if (kind === "snapshot") return "📸";
  if (kind === "recording") return "🎥";
  if (kind === "clip") return "🎬";
  return "📁";
}


function evidenceHeadline(kind: string, t: TFunction): string {
  if (kind === "snapshot") return t("ui.chatPanel.headlineSnapshot");
  if (kind === "recording") return t("ui.chatPanel.headlineRecording");
  if (kind === "clip") return t("ui.chatPanel.headlineClip");
  return t("ui.chatPanel.headlineGeneric", { kind });
}


function TextBubble({
  message,
  onOpenEvidence,
  onSignEvidence,
}: {
  message: ChatMessage;
  onOpenEvidence: (id: string) => void;
  onSignEvidence?: (id: string) => void;
}) {
  // Operator messages on the right, field on the left. We classify by
  // which authorship field is set: author_user → operator (JWT path),
  // author_participant → field (X-Field-Session-Token path).
  const fromOperator = message.author_user !== null;
  return (
    <div
      className={`flex flex-col ${fromOperator ? "items-end" : "items-start"}`}
    >
      <div
        // Chat-tail look: one corner squared on the author's side so the
        // bubble visually "points" at its origin. Rounded-2xl elsewhere
        // for the modern soft-pill feel; shadow-sm gives a hint of depth
        // against the panel background.
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
          fromOperator
            ? "rounded-tr-md bg-nb-mint text-black"
            : "rounded-tl-md bg-nb-surface text-nb-text"
        }`}
      >
        {message.body && (
          <p className="whitespace-pre-wrap break-words leading-snug">
            {message.body}
          </p>
        )}
        {message.attachments.length > 0 && (
          <ul
            className={`space-y-1 ${message.body ? "mt-2" : ""}`}
          >
            {message.attachments.map((a) => (
              <li key={a.id}>
                <AttachmentChip
                  attachment={a}
                  variant={fromOperator ? "on-accent" : "on-slate"}
                  onOpenEvidence={() => onOpenEvidence(a.id)}
                  onSign={onSignEvidence}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className="mt-1 px-1 text-[10px] text-nb-text-3">
        {humanTime(message.created_at)}
      </span>
    </div>
  );
}


function AttachmentChip({
  attachment,
  variant = "on-slate",
  onOpenEvidence,
  onSign,
}: {
  attachment: ChatAttachment;
  /** Tints the chip so it sits well against either bubble background. */
  variant?: "on-accent" | "on-slate";
  onOpenEvidence: () => void;
  /** When set + the attachment is a PDF, renders a Sign button beside
   *  the chip so the operator can dispatch the document for
   *  electronic signature without leaving the chat. */
  onSign?: (evidenceId: string) => void;
}) {
  const { t } = useTranslation();
  const surface =
    variant === "on-accent"
      ? "bg-white/15 hover:bg-white/25 text-white"
      : "bg-black/30 hover:bg-black/40 text-nb-text";
  const sub = variant === "on-accent" ? "text-white/70" : "text-nb-text-2";
  const canSign = !!onSign && attachment.mime === "application/pdf";
  // Full-width sign button reads against either bubble background — mint on
  // the slate field bubble, ink-on-mint on the operator's accent bubble.
  const signTone =
    variant === "on-accent"
      ? "border-black/25 bg-black/10 text-black hover:bg-black/20"
      : "border-nb-mint/40 bg-nb-mint/10 text-nb-mint hover:bg-nb-mint/20";
  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={onOpenEvidence}
        className={`flex w-full min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition ${surface}`}
        aria-label={t("ui.chatPanel.openAttachment", {
          name: attachment.name || attachment.mime,
        })}
      >
        <span
          aria-hidden
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base ${
            variant === "on-accent" ? "bg-white/20" : "bg-black/30"
          }`}
        >
          {mimeIcon(attachment.mime)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {attachment.name || shortMime(attachment.mime)}
          </span>
          <span className={`block text-[10px] ${sub}`}>
            {shortMime(attachment.mime)} · {humanSize(attachment.byte_size)}
          </span>
        </span>
      </button>
      {canSign && onSign && (
        <button
          type="button"
          onClick={() => onSign(attachment.id)}
          className={`flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-[11px] font-medium transition ${signTone}`}
          aria-label={t("ui.chatPanel.signAriaLabel")}
          data-testid="chat-sign-electronically"
        >
          ✍ {t("ui.chatPanel.signElectronically")}
        </button>
      )}
    </div>
  );
}


function mimeIcon(mime: string): string {
  if (mime === "application/pdf") return "📄";
  if (mime.startsWith("image/")) return "🖼";
  if (mime.startsWith("video/")) return "🎞";
  return "📎";
}


function shortMime(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return mime.slice("image/".length).toUpperCase();
  if (mime.startsWith("video/")) return mime.slice("video/".length).toUpperCase();
  return mime;
}


function authorLabel(message: ChatMessage, t: TFunction): string {
  if (message.author_user !== null) return t("ui.chatPanel.authorOperator");
  if (message.author_participant !== null) return t("ui.chatPanel.authorField");
  return t("ui.chatPanel.authorSystem");
}


function humanTime(iso: string): string {
  return formatTime(iso, { hour: "2-digit", minute: "2-digit" });
}


function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}


function Composer({
  draft,
  setDraft,
  pending,
  onAttach,
  onRemoveAttachment,
  onSubmit,
  attaching,
  sending,
}: {
  draft: string;
  setDraft: (v: string) => void;
  pending: ChatAttachment[];
  onAttach: (f: File) => void;
  onRemoveAttachment: (id: string) => void;
  onSubmit: () => void;
  attaching: boolean;
  sending: boolean;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const canSend = (draft.trim().length > 0 || pending.length > 0) && !sending;

  // Auto-grow textarea up to ~5 lines so multi-line drafts stay visible
  // without spawning an internal scrollbar at the wrong moment.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, 132); // ~5 lines @ text-sm
    el.style.height = `${next}px`;
  }, [draft]);

  return (
    <div className="mt-2 border-t border-nb-border pt-2">
      {pending.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {pending.map((a) => (
            <li
              key={a.id}
              className="flex max-w-full items-center gap-2 rounded-lg border border-nb-border bg-nb-bg/70 px-2 py-1.5 text-xs text-nb-text"
            >
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-nb-surface text-sm"
              >
                {mimeIcon(a.mime)}
              </span>
              <span className="min-w-0 flex-1 truncate" title={a.name}>
                {a.name || shortMime(a.mime)}
              </span>
              <span className="shrink-0 text-[10px] text-nb-text-3">
                {humanSize(a.byte_size)}
              </span>
              <button
                type="button"
                onClick={() => onRemoveAttachment(a.id)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-nb-text-3 hover:bg-cvp-danger/20 hover:text-cvp-danger"
                aria-label={t("ui.chatPanel.removeAttachment", {
                  name: a.name || t("ui.chatPanel.attachmentFallback"),
                })}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) onSubmit();
        }}
        // Single pill-shaped container hosts the attach button, the text
        // input, and the send button — visually grouped so it feels like
        // one input affordance, not three disconnected controls.
        className="flex items-end gap-1 rounded-2xl border border-nb-border bg-nb-surface/40 p-1 focus-within:border-nb-mint"
      >
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-nb-text transition hover:bg-nb-surface hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          disabled={attaching}
          onClick={() => fileRef.current?.click()}
          aria-label={t("ui.chatPanel.attachFile")}
          title={t("ui.chatPanel.attachFile")}
        >
          {attaching ? (
            <span
              aria-hidden
              className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"
            />
          ) : (
            <PaperclipIcon />
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onAttach(f);
            // Reset so the same filename can be picked twice in a row.
            e.target.value = "";
          }}
        />
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSubmit();
            }
          }}
          rows={1}
          placeholder={t("ui.chatPanel.composerPlaceholder")}
          // text-base (16px) — iOS Safari auto-zooms the viewport
          // when an input has font-size < 16px. text-sm (14px) was
          // triggering that on the field's phone every time the
          // composer got focus. 16px keeps the keyboard input
          // ergonomic without yanking the page in.
          className="min-h-9 flex-1 resize-none bg-transparent px-2 py-1.5 text-base text-nb-text placeholder:text-nb-text-3 focus:outline-none"
        />
        <button
          type="submit"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-nb-mint text-black transition disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-nb-text-3"
          disabled={!canSend}
          aria-label={t("ui.chatPanel.sendMessage")}
        >
          {sending ? (
            <span
              aria-hidden
              className="h-3 w-3 animate-spin rounded-full border-2 border-white/80 border-t-transparent"
            />
          ) : (
            <SendIcon />
          )}
        </button>
      </form>
    </div>
  );
}


function PaperclipIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}


function SendIcon() {
  // Tilted-airplane glyph; rotated subtly so it leans toward the send
  // direction (right) and reads as motion.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M3.4 20.4 22 12 3.4 3.6 3.4 10l13 2-13 2z" />
    </svg>
  );
}
